import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectUsername, selectName, selectUserType, selectLogoUrl, selectSportsClubId, setLogoUrl } from '../store/userSlice';
import { AuthenticationService, SportsClubControllerService } from '../api';
import { OpenAPI } from '../api/core/OpenAPI';
import { toast } from 'react-toastify';
import axios from 'axios';
import './AccountDetails.css';

const MAX_LOGO_SIZE_BYTES = 2 * 1024 * 1024; // 2 MB
const ALLOWED_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const PNG_MAGIC = [0x89, 0x50, 0x4E, 0x47];
const JPEG_MAGIC = [0xFF, 0xD8, 0xFF];
const WEBP_MAGIC_RIFF = [0x52, 0x49, 0x46, 0x46];

const validateFileMagicBytes = (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const arr = new Uint8Array(e.target.result);
      const isPng = PNG_MAGIC.every((b, i) => arr[i] === b);
      const isJpeg = JPEG_MAGIC.every((b, i) => arr[i] === b);
      const isWebp = WEBP_MAGIC_RIFF.every((b, i) => arr[i] === b);
      resolve(isPng || isJpeg || isWebp);
    };
    reader.onerror = () => resolve(false);
    reader.readAsArrayBuffer(file.slice(0, 12));
  });
};

function AccountDetails() {
  const dispatch = useDispatch();
  const username = useSelector(selectUsername);
  const name = useSelector(selectName);
  const userType = useSelector(selectUserType);
  const logoUrl = useSelector(selectLogoUrl);
  const sportsClubId = useSelector(selectSportsClubId);

  const [clubDetails, setClubDetails] = useState(null);
  const [loadingClubDetails, setLoadingClubDetails] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoError, setLogoError] = useState('');
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (userType === 'SPORTS_CLUB' && sportsClubId) {
      setLoadingClubDetails(true);
      SportsClubControllerService.getMyDetails()
        .then(data => setClubDetails(data))
        .catch(() => {})
        .finally(() => setLoadingClubDetails(false));
    }
  }, [userType, sportsClubId]);

  const userTypeLabel = userType === 'SPORTS_CLUB' ? 'Klub sportowy' : 'Trener';
  const nameLabel = userType === 'SPORTS_CLUB' ? 'Nazwa' : 'Imię i nazwisko';

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    setLogoError('');

    if (!file) {
      setLogoFile(null);
      setLogoPreview(null);
      return;
    }

    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      setLogoError('Logo musi być w formacie PNG, JPEG lub WebP');
      e.target.value = '';
      return;
    }

    if (file.size > MAX_LOGO_SIZE_BYTES) {
      setLogoError(`Maksymalny rozmiar pliku to 2 MB. Wybrany plik: ${(file.size / 1024 / 1024).toFixed(1)} MB`);
      e.target.value = '';
      return;
    }

    const isValidContent = await validateFileMagicBytes(file);
    if (!isValidContent) {
      setLogoError('Plik nie jest prawidłowym obrazem. Wybierz plik PNG, JPEG lub WebP.');
      e.target.value = '';
      return;
    }

    if (logoPreview) {
      URL.revokeObjectURL(logoPreview);
    }

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      if (img.width > 1024 || img.height > 1024) {
        setLogoError(`Maksymalna rozdzielczość to 1024x1024 px. Wybrany obraz: ${img.width}x${img.height} px`);
        setLogoFile(null);
        setLogoPreview(null);
        URL.revokeObjectURL(objectUrl);
        e.target.value = '';
      } else {
        setLogoFile(file);
        setLogoPreview(objectUrl);
      }
    };
    img.onerror = () => {
      setLogoError('Nie udało się odczytać pliku graficznego');
      URL.revokeObjectURL(objectUrl);
      e.target.value = '';
    };
    img.src = objectUrl;
  };

  const handleRemoveLogo = () => {
    if (logoPreview) {
      URL.revokeObjectURL(logoPreview);
    }
    setLogoFile(null);
    setLogoPreview(null);
    setLogoError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) return;

    setIsUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('image', logoFile);

      const response = await axios.put(`${OpenAPI.BASE}/update-logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });

      dispatch(setLogoUrl(response.data.logoUrl));
      toast.success('Logo zostało zaktualizowane');
      handleRemoveLogo();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Nieprawidłowy plik. Sprawdź format i rozmiar.');
      } else {
        toast.error('Wystąpił błąd podczas aktualizacji logo');
      }
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error('Nowe hasła nie są identyczne');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Nowe hasło musi mieć co najmniej 8 znaków');
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      toast.error('Nowe hasło musi zawierać co najmniej jedną wielką literę');
      return;
    }
    if (!/[a-z]/.test(newPassword)) {
      toast.error('Nowe hasło musi zawierać co najmniej jedną małą literę');
      return;
    }
    if (!/[0-9]/.test(newPassword)) {
      toast.error('Nowe hasło musi zawierać co najmniej jedną cyfrę');
      return;
    }

    setIsSubmitting(true);
    try {
      await AuthenticationService.changePassword({
        currentPassword,
        newPassword,
      });
      toast.success('Hasło zostało zmienione');
      setShowPasswordForm(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      if (error.status === 401) {
        toast.error('Aktualne hasło jest nieprawidłowe');
      } else {
        toast.error('Wystąpił błąd podczas zmiany hasła');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="account-container">
      <div className="account-card">
        <div className="account-header">
          <div className="account-avatar">
            <span>{userType === 'SPORTS_CLUB' ? '🏢' : '👤'}</span>
          </div>
          <h1>Dane konta</h1>
          <p>Informacje o Twoim koncie</p>
        </div>

        <div className="account-content">
          <div className="account-info-grid">
            <div className="account-info-item">
              <div className="info-icon">{userType === 'SPORTS_CLUB' ? '🏢' : '⚽'}</div>
              <div className="info-details">
                <span className="info-label">Typ konta</span>
                <span className="info-value">{userTypeLabel}</span>
              </div>
            </div>

            <div className="account-info-item">
              <div className="info-icon">🔑</div>
              <div className="info-details">
                <span className="info-label">Login</span>
                <span className="info-value">{username}</span>
              </div>
            </div>

            <div className="account-info-item">
              <div className="info-icon">👤</div>
              <div className="info-details">
                <span className="info-label">{nameLabel}</span>
                <span className="info-value">{name}</span>
              </div>
            </div>
          </div>

          {userType === 'SPORTS_CLUB' && clubDetails && (
            <div className="account-club-details-section">
              <h3 className="club-details-title">Dane klubu sportowego</h3>
              <div className="account-info-grid">
                {clubDetails.nip && (
                  <div className="account-info-item">
                    <div className="info-icon">📋</div>
                    <div className="info-details">
                      <span className="info-label">NIP</span>
                      <span className="info-value">{clubDetails.nip}</span>
                    </div>
                  </div>
                )}
                {clubDetails.regon && (
                  <div className="account-info-item">
                    <div className="info-icon">📋</div>
                    <div className="info-details">
                      <span className="info-label">REGON</span>
                      <span className="info-value">{clubDetails.regon}</span>
                    </div>
                  </div>
                )}
                {clubDetails.voivodeship && (
                  <div className="account-info-item">
                    <div className="info-icon">📍</div>
                    <div className="info-details">
                      <span className="info-label">Województwo</span>
                      <span className="info-value">{clubDetails.voivodeship}</span>
                    </div>
                  </div>
                )}
                {clubDetails.county && (
                  <div className="account-info-item">
                    <div className="info-icon">📍</div>
                    <div className="info-details">
                      <span className="info-label">Powiat</span>
                      <span className="info-value">{clubDetails.county}</span>
                    </div>
                  </div>
                )}
                {clubDetails.city && (
                  <div className="account-info-item">
                    <div className="info-icon">🏟️</div>
                    <div className="info-details">
                      <span className="info-label">Adres boiska</span>
                      <span className="info-value">{clubDetails.city}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {userType === 'SPORTS_CLUB' && loadingClubDetails && (
            <div className="account-club-details-section">
              <h3 className="club-details-title">Dane klubu sportowego</h3>
              <p className="loading-text">Ładowanie danych...</p>
            </div>
          )}

          {userType === 'SPORTS_CLUB' && (
            <div className="account-logo-section">
              <h3 className="logo-section-title">Logo klubu</h3>
              <div className="logo-current">
                {logoUrl ? (
                  <img src={logoUrl} alt="Logo klubu" className="logo-current-img" />
                ) : (
                  <div className="logo-placeholder">
                    <span>🏢</span>
                    <p>Brak logo</p>
                  </div>
                )}
              </div>
              {logoPreview ? (
                <div className="logo-new-preview">
                  <p className="logo-new-label">Nowe logo:</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <img
                      src={logoPreview}
                      alt="Podgląd nowego logo"
                      className="logo-preview-img"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveLogo}
                      className="btn-remove-logo"
                    >
                      Usuń
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={handleLogoUpload}
                    className="btn-upload-logo"
                    disabled={isUploadingLogo}
                  >
                    {isUploadingLogo ? 'Zapisywanie...' : 'Zapisz logo'}
                  </button>
                </div>
              ) : (
                <div className="logo-upload-input">
                  <label htmlFor="logoInput" className="btn-choose-logo">
                    {logoUrl ? 'Zmień logo' : 'Dodaj logo'}
                  </label>
                  <input
                    type="file"
                    id="logoInput"
                    ref={fileInputRef}
                    accept="image/png,image/jpeg,image/webp"
                    onChange={handleLogoChange}
                    style={{ display: 'none' }}
                  />
                </div>
              )}
              {logoError && <span className="logo-error-message">{logoError}</span>}
              <small className="logo-hint">
                Format: PNG, JPEG lub WebP. Maks. rozmiar: 2 MB. Maks. rozdzielczość: 1024x1024 px.
              </small>
            </div>
          )}

          <div className="account-password-section">
            {!showPasswordForm ? (
              <button
                className="btn-change-password"
                onClick={() => setShowPasswordForm(true)}
              >
                Zmień hasło
              </button>
            ) : (
              <form className="password-form" onSubmit={handlePasswordChange}>
                <h3 className="password-form-title">Zmiana hasła</h3>

                <div className="form-group">
                  <label className="form-label">Aktualne hasło</label>
                  <input
                    type="password"
                    className="form-input"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    maxLength={128}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Nowe hasło</label>
                  <input
                    type="password"
                    className="form-input"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    maxLength={128}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Potwierdź nowe hasło</label>
                  <input
                    type="password"
                    className="form-input"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    maxLength={128}
                  />
                </div>

                <div className="password-form-actions">
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Zapisywanie...' : 'Zapisz'}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => {
                      setShowPasswordForm(false);
                      setCurrentPassword('');
                      setNewPassword('');
                      setConfirmPassword('');
                    }}
                    disabled={isSubmitting}
                  >
                    Anuluj
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AccountDetails;
